import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useChild } from "../context/ChildContext";
import { sendChildNotification } from "../utils/notificationHelper";
import EditEntryDialog from "../components/tracker/EditEntryDialog";

const HOUR_MARKERS = Array.from({ length: 24 }, (_, i) => i);
const TIMELINE_MINUTES = 1440;

function getPos(minutes) {
  return (minutes / TIMELINE_MINUTES) * 100;
}

function getMinutesFromMidnight(isoString) {
  const d = new Date(isoString);
  return d.getHours() * 60 + d.getMinutes();
}

function formatDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}t ${m}m`;
  if (h > 0) return `${h}t`;
  return `${m}m`;
}

function formatTime(isoString) {
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function useTodaySleepData(childId) {
  const [sleeps, setSleeps] = useState([]);
  const [activeSleep, setActiveSleep] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!childId) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const sleepsQuery = query(
      collection(db, `children/${childId}/sleeps`),
      where("start", ">=", yesterday.toISOString()),
    );

    const unsubSleeps = onSnapshot(sleepsQuery, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSleeps(data);
    });

    const unsubActive = onSnapshot(
      collection(db, `children/${childId}/activeSleep`),
      (snap) => {
        if (!snap.empty) {
          const d = snap.docs[0];
          setActiveSleep({ id: d.id, ...d.data(), active: true });
        } else {
          setActiveSleep(null);
        }
      },
    );

    return () => {
      unsubSleeps();
      unsubActive();
    };
  }, [childId]);

  const startSleep = async () => {
    setLoading(true);
    try {
      const now = new Date().toISOString();
      await addDoc(collection(db, `children/${childId}/activeSleep`), {
        start: now,
        created: now,
        headPosition: "middle",
      });
      sendChildNotification(childId, "sleep_started");
    } finally {
      setLoading(false);
    }
  };

  const stopSleep = async () => {
    if (!activeSleep) return;
    setLoading(true);
    try {
      await addDoc(collection(db, `children/${childId}/sleeps`), {
        start: activeSleep.start,
        end: new Date().toISOString(),
        created: activeSleep.created,
        headPosition: activeSleep.headPosition || "middle",
      });
      await deleteDoc(
        doc(db, `children/${childId}/activeSleep`, activeSleep.id),
      );
      sendChildNotification(childId, "sleep_stopped");
    } finally {
      setLoading(false);
    }
  };

  return { sleeps, activeSleep, loading, startSleep, stopSleep };
}

// Renders a single sleep bar with tooltip. Supports overnight wrap-around.
function SleepBar({ startMins, durationMins, sx, tooltip, onClick }) {
  const endsAfterMidnight = startMins + durationMins > TIMELINE_MINUTES;

  const barSx = {
    position: "absolute",
    height: 40,
    top: 10,
    borderRadius: "4px",
    cursor: onClick ? "pointer" : "default",
    "&:hover": onClick ? { filter: "brightness(1.12)" } : {},
    ...sx,
  };

  if (endsAfterMidnight) {
    return (
      <>
        <Tooltip
          title={tooltip}
          placement="top"
          arrow
        >
          <Box
            onClick={onClick}
            sx={{
              ...barSx,
              left: `${getPos(startMins)}%`,
              width: `${getPos(TIMELINE_MINUTES - startMins)}%`,
            }}
          />
        </Tooltip>
        <Tooltip
          title={tooltip}
          placement="top"
          arrow
        >
          <Box
            onClick={onClick}
            sx={{
              ...barSx,
              left: 0,
              width: `${getPos(startMins + durationMins - TIMELINE_MINUTES)}%`,
            }}
          />
        </Tooltip>
      </>
    );
  }

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow
    >
      <Box
        onClick={onClick}
        sx={{
          ...barSx,
          left: `${getPos(startMins)}%`,
          width: `${getPos(durationMins)}%`,
        }}
      />
    </Tooltip>
  );
}

function ChildSleepRow({ child, now }) {
  const { sleeps, activeSleep, loading, startSleep, stopSleep } =
    useTodaySleepData(child.id);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingValue, setEditingValue] = useState({});

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayISO = today.toISOString();

  const todaySleeps = sleeps.filter((s) => {
    if (!s.end) return false;
    if (s.start >= todayISO) return true;
    return new Date(s.end) >= today;
  });

  const nowMins = new Date(now).getHours() * 60 + new Date(now).getMinutes();

  const activeDurationMinutes = activeSleep
    ? Math.max(0, Math.floor((now - new Date(activeSleep.start)) / 60000))
    : 0;
  const activeStartMins = activeSleep
    ? getMinutesFromMidnight(activeSleep.start)
    : 0;

  const openEdit = (entry) => {
    setEditingEntry({ ...entry, type: "Sleep" });
    setEditingValue({
      start: entry.start,
      end: entry.end || new Date().toISOString(),
      headPosition: entry.headPosition || "middle",
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingEntry) return;
    const newStart = new Date(editingValue.start).toISOString();
    const headPosition = editingValue.headPosition || "middle";
    if (editingEntry.active) {
      await updateDoc(
        doc(db, `children/${child.id}/activeSleep`, editingEntry.id),
        { start: newStart, headPosition },
      );
    } else {
      await updateDoc(doc(db, `children/${child.id}/sleeps`, editingEntry.id), {
        start: newStart,
        end: new Date(editingValue.end).toISOString(),
        headPosition,
      });
    }
    setEditDialogOpen(false);
    setEditingEntry(null);
  };

  const handleDelete = async () => {
    if (!editingEntry) return;
    const col = editingEntry.active
      ? `children/${child.id}/activeSleep`
      : `children/${child.id}/sleeps`;
    await deleteDoc(doc(db, col, editingEntry.id));
    setEditDialogOpen(false);
    setEditingEntry(null);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {/* Child name + action chip */}
      <Box sx={{ width: 72, flexShrink: 0, pr: 1 }}>
        <Tooltip title={activeSleep ? "Stop søvn" : "Start søvn"}>
          <Chip
            label={child.name}
            size="small"
            onClick={loading ? undefined : activeSleep ? stopSleep : startSleep}
            icon={
              loading ? (
                <CircularProgress size={12} sx={{ color: "inherit !important" }} />
              ) : activeSleep ? (
                <StopCircleOutlinedIcon />
              ) : (
                <BedtimeIcon />
              )
            }
            sx={{
              width: "100%",
              fontWeight: 700,
              fontSize: "0.7rem",
              backgroundColor: activeSleep ? "#FFB74D" : "#EDE7F6",
              color: activeSleep ? "#7E3700" : "#5E35B1",
              cursor: loading ? "default" : "pointer",
              "& .MuiChip-icon": {
                fontSize: 14,
                color: activeSleep ? "#7E3700" : "#5E35B1",
              },
              "&:hover": loading
                ? {}
                : { filter: "brightness(0.93)" },
            }}
          />
        </Tooltip>
      </Box>

      {/* Timeline */}
      <Box sx={{ flex: 1, position: "relative", height: 60 }}>
        {/* Hour grid */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            display: "flex",
          }}
        >
          {HOUR_MARKERS.map((h) => (
            <Box
              key={h}
              sx={{
                flex: 1,
                borderLeft: h % 6 === 0 ? "2px solid #ddd" : "1px solid #eee",
                position: "relative",
              }}
            >
              {h % 3 === 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: -18,
                    left: -8,
                    fontSize: "0.65rem",
                    color: "text.secondary",
                  }}
                >
                  {h.toString().padStart(2, "0")}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Completed sleeps */}
        {todaySleeps.map((s) => {
          const start = new Date(s.start);
          const end = new Date(s.end);
          const startMins = start < today ? 0 : getMinutesFromMidnight(s.start);
          const durationMs = end - (start < today ? today : start);
          const durationMins = Math.floor(durationMs / 60000);
          if (durationMins <= 0) return null;
          return (
            <SleepBar
              key={s.id}
              startMins={startMins}
              durationMins={durationMins}
              sx={{
                backgroundColor: "#7E57C2",
                opacity: 0.7,
                border: "2px solid #5E35B1",
                zIndex: 3,
              }}
              tooltip={
                <Box>
                  <div>
                    {formatTime(s.start)} – {formatTime(s.end)}
                  </div>
                  <div>{formatDuration(durationMins)}</div>
                </Box>
              }
              onClick={() => openEdit(s)}
            />
          );
        })}

        {/* Active sleep */}
        {activeSleep && (
          <Chip
            label={formatDuration(activeDurationMinutes)}
            size="small"
            sx={{
              position: "absolute",
              left: `${getPos(activeStartMins + activeDurationMinutes / 2)}%`,
              transform: "translateX(-50%)",
              top: 0,
              height: 16,
              backgroundColor: "#F57C00",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.6rem",
              zIndex: 20,
              pointerEvents: "none",
              "& .MuiChip-label": { px: "5px" },
            }}
          />
        )}
        {activeSleep && (
          <SleepBar
            startMins={activeStartMins}
            durationMins={activeDurationMinutes}
            sx={{
              backgroundColor: "#FFB74D",
              opacity: 0.9,
              border: "2px solid #F57C00",
              zIndex: 5,
              animation: "pulse-bar 2s ease-in-out infinite",
              "@keyframes pulse-bar": {
                "0%, 100%": { boxShadow: "0 0 0 0 rgba(245,124,0,0.7)" },
                "50%": { boxShadow: "0 0 10px 2px rgba(245,124,0,0.4)" },
              },
            }}
            tooltip={
              <Box>
                <div>{formatTime(activeSleep.start)} –</div>
                <div>{formatDuration(activeDurationMinutes)}</div>
              </Box>
            }
            onClick={() => openEdit(activeSleep)}
          />
        )}

        {/* Current time indicator */}
        <Box
          sx={{
            position: "absolute",
            left: `${getPos(nowMins)}%`,
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: "#F57C00",
            zIndex: 10,
          }}
        />
      </Box>

      {/* Edit dialog */}
      <EditEntryDialog
        open={editDialogOpen}
        editingEntry={editingEntry}
        editingValue={editingValue}
        setEditingValue={setEditingValue}
        onSave={handleSave}
        onDelete={handleDelete}
        onCancel={() => {
          setEditDialogOpen(false);
          setEditingEntry(null);
        }}
        editingImageFile={null}
        setEditingImageFile={() => {}}
        editingImagePreview={null}
        setEditingImagePreview={() => {}}
      />
    </Box>
  );
}
const MOCK_CHILDREN = [
  {
    name: "Emma",
    sleeps: [{ startMins: 1290, durationMins: 510 }, { startMins: 780, durationMins: 45 }],
    active: null,
  },
  {
    name: "Noah",
    sleeps: [{ startMins: 1320, durationMins: 480 }],
    active: { startMins: 840, durationMins: 35 },
  },
  {
    name: "Sofie",
    sleeps: [{ startMins: 0, durationMins: 390 }, { startMins: 720, durationMins: 60 }],
    active: null,
  },
  {
    name: "Maja",
    sleeps: [{ startMins: 0, durationMins: 420 }, { startMins: 750, durationMins: 90 }],
    active: { startMins: 960, durationMins: 20 },
  },
  {
    name: "Oliver",
    sleeps: [{ startMins: 1350, durationMins: 450 }, { startMins: 810, durationMins: 30 }],
    active: null,
  },
  {
    name: "Ida",
    sleeps: [{ startMins: 0, durationMins: 360 }],
    active: { startMins: 870, durationMins: 55 },
  },
  {
    name: "Lukas",
    sleeps: [{ startMins: 1260, durationMins: 540 }],
    active: null,
  },
  {
    name: "Astrid",
    sleeps: [{ startMins: 1300, durationMins: 500 }, { startMins: 690, durationMins: 75 }],
    active: null,
  },
  {
    name: "Victor",
    sleeps: [{ startMins: 0, durationMins: 450 }, { startMins: 800, durationMins: 40 }, { startMins: 990, durationMins: 25 }],
    active: null,
  },
  {
    name: "Laura",
    sleeps: [{ startMins: 1310, durationMins: 470 }],
    active: { startMins: 930, durationMins: 15 },
  },
  {
    name: "Magnus",
    sleeps: [{ startMins: 0, durationMins: 400 }, { startMins: 760, durationMins: 50 }],
    active: null,
  },
  {
    name: "Freja",
    sleeps: [{ startMins: 1270, durationMins: 530 }],
    active: { startMins: 750, durationMins: 45 },
  },
];

function MockChildSleepRow({ mock, now }) {
  const nowMins = new Date(now).getHours() * 60 + new Date(now).getMinutes();
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: 72, flexShrink: 0, pr: 1 }}>
        <Chip
          label={mock.name}
          size="small"
          icon={mock.active ? <StopCircleOutlinedIcon /> : <BedtimeIcon />}
          sx={{
            width: "100%",
            fontWeight: 700,
            fontSize: "0.7rem",
            backgroundColor: mock.active ? "#FFB74D" : "#EDE7F6",
            color: mock.active ? "#7E3700" : "#5E35B1",
            cursor: "default",
            "& .MuiChip-icon": { fontSize: 14, color: mock.active ? "#7E3700" : "#5E35B1" },
          }}
        />
      </Box>
      <Box sx={{ flex: 1, position: "relative", height: 60 }}>
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "100%", display: "flex" }}>
          {HOUR_MARKERS.map((h) => (
            <Box key={h} sx={{ flex: 1, borderLeft: h % 6 === 0 ? "2px solid #ddd" : "1px solid #eee", position: "relative" }}>
              {h % 3 === 0 && (
                <Typography variant="caption" sx={{ position: "absolute", top: -18, left: -8, fontSize: "0.65rem", color: "text.secondary" }}>
                  {h.toString().padStart(2, "0")}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        {mock.sleeps.map((s, i) => (
          <SleepBar key={i} startMins={s.startMins} durationMins={s.durationMins}
            sx={{ backgroundColor: "#7E57C2", opacity: 0.7, border: "2px solid #5E35B1", zIndex: 3 }}
            tooltip={<Box><div>{formatDuration(s.durationMins)}</div></Box>}
          />
        ))}
        {mock.active && (
          <Chip
            label={formatDuration(mock.active.durationMins)}
            size="small"
            sx={{
              position: "absolute",
              left: `${getPos(mock.active.startMins + mock.active.durationMins / 2)}%`,
              transform: "translateX(-50%)",
              top: 0, height: 16,
              backgroundColor: "#F57C00", color: "#fff",
              fontWeight: 700, fontSize: "0.6rem",
              zIndex: 20, pointerEvents: "none",
              "& .MuiChip-label": { px: "5px" },
            }}
          />
        )}
        {mock.active && (
          <SleepBar startMins={mock.active.startMins} durationMins={mock.active.durationMins}
            sx={{ backgroundColor: "#FFB74D", opacity: 0.9, border: "2px solid #F57C00", zIndex: 5,
              animation: "pulse-bar 2s ease-in-out infinite",
              "@keyframes pulse-bar": { "0%, 100%": { boxShadow: "0 0 0 0 rgba(245,124,0,0.7)" }, "50%": { boxShadow: "0 0 10px 2px rgba(245,124,0,0.4)" } },
            }}
            tooltip={<Box><div>{formatDuration(mock.active.durationMins)}</div></Box>}
          />
        )}
        <Box sx={{ position: "absolute", left: `${getPos(nowMins)}%`, top: 0, bottom: 0, width: 2, backgroundColor: "#F57C00", zIndex: 10 }} />
      </Box>
    </Box>
  );
}

const SleepOverviewPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { childList } = useChild();
  const [now, setNow] = useState(Date.now());
  const [showMock, setShowMock] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.page", pt: 8 }}>
      <Container maxWidth="sm">
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}>
          <Tooltip title={t("nav.back")}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" fontWeight={600}>
            {t("sleep_overview.title")}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showMock}
                onChange={(e) => setShowMock(e.target.checked)}
              />
            }
            label={<Typography variant="caption" color="text.secondary">Mock</Typography>}
            sx={{ ml: "auto", mr: 0 }}
          />
        </Box>

        <Paper
          elevation={10}
          sx={{ borderRadius: 2, overflow: "hidden" }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              {t("sleep_overview.section_today")}
            </Typography>
          </Box>
          <Box sx={{ px: 2, pt: 3, pb: 2 }}>
            {childList.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={2}
              >
                {t("sleep_overview.no_children")}
              </Typography>
            )}
            {[
              ...childList.map((child) => (
                <ChildSleepRow
                  key={child.id}
                  child={child}
                  now={now}
                />
              )),
              ...(showMock ? MOCK_CHILDREN.map((mock) => (
                <MockChildSleepRow key={mock.name} mock={mock} now={now} />
              )) : []),
            ].map((row, i, arr) => (
              <React.Fragment key={row.key}>
                {row}
                {i < arr.length - 1 && <Divider sx={{ mt: 1, mb: 3 }} />}
              </React.Fragment>
            ))}

            {/* Legend */}
            <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    bgcolor: "#7E57C2",
                    borderRadius: "3px",
                    opacity: 0.7,
                    border: "2px solid #5E35B1",
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {t("sleep_overview.legend_sleep")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    bgcolor: "#FFB74D",
                    borderRadius: "3px",
                    opacity: 0.9,
                    border: "2px solid #F57C00",
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {t("sleep_overview.legend_active")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 2,
                    height: 14,
                    bgcolor: "#F57C00",
                    borderRadius: "1px",
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {t("sleep_overview.legend_now")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SleepOverviewPage;
